import { ANNOTATION_FREEHAND_POINTS_MAX, type AnnotationShape } from "@rakazo/contracts";
import type { RelativeRect } from "./pdf-render";

/*
 * RAK-168: geometry of the marks drawn on the PDF preview. Everything here works in page
 * coordinates relative 0..1 with the origin top left, as the contract stores them, so a mark
 * keeps its place at any zoom and any panel width. No DOM, no React: the view feeds pointer
 * positions in and draws what comes out.
 */

/** A point on the page, 0..1 each, origin top left. */
export type RelPoint = [number, number];

/** The drawing tools of the toolbar, named like the contract's shape kinds. */
export type MarkupTool = AnnotationShape["kind"];

/** Tools in toolbar order. */
export const MARKUP_TOOLS: readonly MarkupTool[] = [
  "rect",
  "ellipse",
  "arrow",
  "freehand",
  "highlight",
];

/** Colours offered for a new mark; the first is the default. Lowercase #rrggbb as the contract wants. */
export const MARKUP_COLORS = ["#e5484d", "#f5a524", "#3e63dd", "#30a46c"] as const;

/** Colour of the highlighter, a soft yellow drawn translucent. */
export const HIGHLIGHT_COLOR = "#ffd60a";

/** Drags shorter than this many CSS pixels count as a click, not a mark. */
export const MIN_DRAG_PX = 6;

/** Freehand points closer than this many CSS pixels to the last kept one are dropped. */
export const FREEHAND_STEP_PX = 2;

/** Clamps a number into 0..1. */
export function clamp01(value: number): number {
  if (Number.isNaN(value)) return 0;
  return Math.min(1, Math.max(0, value));
}

/** Converts a pointer position (client pixels) to a page point, clamped to the page box. */
export function toPagePoint(
  clientX: number,
  clientY: number,
  box: { left: number; top: number; width: number; height: number },
): RelPoint {
  if (box.width <= 0 || box.height <= 0) return [0, 0];
  return [clamp01((clientX - box.left) / box.width), clamp01((clientY - box.top) / box.height)];
}

/** Distance between two page points in CSS pixels of a page drawn at the given size. */
export function pixelDistance(a: RelPoint, b: RelPoint, size: { width: number; height: number }) {
  return Math.hypot((a[0] - b[0]) * size.width, (a[1] - b[1]) * size.height);
}

/**
 * Builds the shape of a finished two-point drag, or null when the drag was too short to mean
 * anything (a stray click must not leave an invisible mark behind).
 */
export function shapeFromDrag(
  tool: Exclude<MarkupTool, "freehand">,
  start: RelPoint,
  end: RelPoint,
  color: string,
  size: { width: number; height: number },
): AnnotationShape | null {
  if (pixelDistance(start, end, size) < MIN_DRAG_PX) return null;
  const points: [RelPoint, RelPoint] = [
    [clamp01(start[0]), clamp01(start[1])],
    [clamp01(end[0]), clamp01(end[1])],
  ];
  return { kind: tool, points, color: tool === "highlight" ? HIGHLIGHT_COLOR : color };
}

/**
 * Adds a pointer position to a freehand stroke: points closer than FREEHAND_STEP_PX to the last
 * kept one are dropped, and the stroke never grows past the contract's point limit. Returns the
 * same array when nothing was added.
 */
export function appendFreehandPoint(
  points: readonly RelPoint[],
  next: RelPoint,
  size: { width: number; height: number },
): readonly RelPoint[] {
  const last = points.at(-1);
  if (last && pixelDistance(last, next, size) < FREEHAND_STEP_PX) return points;
  if (points.length >= ANNOTATION_FREEHAND_POINTS_MAX) return points;
  return [...points, [clamp01(next[0]), clamp01(next[1])]];
}

/** Finishes a freehand stroke, or null when it is too short to be a mark. */
export function shapeFromStroke(
  points: readonly RelPoint[],
  color: string,
  size: { width: number; height: number },
): AnnotationShape | null {
  if (points.length < 2) return null;
  let length = 0;
  for (let i = 1; i < points.length; i += 1) {
    length += pixelDistance(points[i - 1] as RelPoint, points[i] as RelPoint, size);
  }
  if (length < MIN_DRAG_PX) return null;
  return { kind: "freehand", points: points.map((p) => [p[0], p[1]] as RelPoint), color };
}

/** Smallest page rectangle holding every point of a shape. */
export function shapeBounds(shape: AnnotationShape): RelativeRect {
  let x0 = 1;
  let y0 = 1;
  let x1 = 0;
  let y1 = 0;
  for (const [x, y] of shape.points) {
    x0 = Math.min(x0, x);
    y0 = Math.min(y0, y);
    x1 = Math.max(x1, x);
    y1 = Math.max(y1, y);
  }
  return { x0, y0, x1, y1 };
}

/**
 * The page area sent to the bot as excerpt: the shape's bounds plus a margin of `marginPx` CSS
 * pixels, so a thin line or arrow still shows its surroundings, clamped to the page.
 */
export function excerptRect(
  shape: AnnotationShape,
  size: { width: number; height: number },
  marginPx = 24,
): RelativeRect {
  const b = shapeBounds(shape);
  const mx = size.width > 0 ? marginPx / size.width : 0;
  const my = size.height > 0 ? marginPx / size.height : 0;
  return {
    x0: clamp01(b.x0 - mx),
    y0: clamp01(b.y0 - my),
    x1: clamp01(b.x1 + mx),
    y1: clamp01(b.y1 + my),
  };
}

/** Where the comment field of a shape sits: under its lower-left corner, page-relative. */
export function commentAnchor(shape: AnnotationShape): RelPoint {
  const b = shapeBounds(shape);
  return [b.x0, b.y1];
}

/**
 * The two back strokes of an arrow head in page coordinates, sized in CSS pixels so the head
 * looks the same on wide and narrow pages. Returns [left, right] end points.
 */
export function arrowHead(
  tail: RelPoint,
  head: RelPoint,
  size: { width: number; height: number },
  lengthPx = 12,
  spread = Math.PI / 7,
): [RelPoint, RelPoint] {
  const dx = (head[0] - tail[0]) * size.width;
  const dy = (head[1] - tail[1]) * size.height;
  const angle = Math.atan2(dy, dx);
  const back = (turn: number): RelPoint => [
    head[0] - (Math.cos(angle + turn) * lengthPx) / (size.width || 1),
    head[1] - (Math.sin(angle + turn) * lengthPx) / (size.height || 1),
  ];
  return [back(spread), back(-spread)];
}

/** SVG path data for a freehand stroke in a viewBox of the page's CSS size. */
export function freehandPath(points: readonly RelPoint[], size: { width: number; height: number }) {
  return points
    .map(([x, y], i) => `${i === 0 ? "M" : "L"}${round(x * size.width)} ${round(y * size.height)}`)
    .join(" ");
}

/** Rounds to two decimals so SVG attributes stay short and stable in tests. */
function round(value: number): number {
  return Math.round(value * 100) / 100;
}

/**
 * Whether a page point hits a shape, with a tolerance in CSS pixels; used to pick a mark for
 * its comment. Areas (rect, ellipse, highlight) hit inside, lines near the stroke.
 */
export function hitsShape(
  shape: AnnotationShape,
  point: RelPoint,
  size: { width: number; height: number },
  tolerancePx = 6,
): boolean {
  const px = (p: RelPoint): [number, number] => [p[0] * size.width, p[1] * size.height];
  const [x, y] = px(point);
  if (shape.kind === "rect" || shape.kind === "highlight" || shape.kind === "ellipse") {
    const b = shapeBounds(shape);
    const left = b.x0 * size.width - tolerancePx;
    const right = b.x1 * size.width + tolerancePx;
    const top = b.y0 * size.height - tolerancePx;
    const bottom = b.y1 * size.height + tolerancePx;
    if (shape.kind !== "ellipse") return x >= left && x <= right && y >= top && y <= bottom;
    const cx = (left + right) / 2;
    const cy = (top + bottom) / 2;
    const rx = (right - left) / 2;
    const ry = (bottom - top) / 2;
    if (rx <= 0 || ry <= 0) return false;
    return ((x - cx) / rx) ** 2 + ((y - cy) / ry) ** 2 <= 1;
  }
  for (let i = 1; i < shape.points.length; i += 1) {
    const a = px(shape.points[i - 1] as RelPoint);
    const b = px(shape.points[i] as RelPoint);
    if (segmentDistance([x, y], a, b) <= tolerancePx) return true;
  }
  return false;
}

/** Distance from point p to the segment a–b, all in pixels. */
function segmentDistance(p: [number, number], a: [number, number], b: [number, number]) {
  const dx = b[0] - a[0];
  const dy = b[1] - a[1];
  const lengthSq = dx * dx + dy * dy;
  const t = lengthSq === 0 ? 0 : clamp01(((p[0] - a[0]) * dx + (p[1] - a[1]) * dy) / lengthSq);
  return Math.hypot(p[0] - (a[0] + t * dx), p[1] - (a[1] + t * dy));
}
